import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown, ChevronUp, FlaskConical, Loader2, Pause, Play, RotateCcw, SkipForward, Square, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { toUserMessage } from '@/lib/transient';
import { formatDateTime } from '../format';
import type { SimStatus, SimStep } from './api';
import { PLAY_EVERY_MS, STEPS, canStep, progressOf } from './format';
import { SimTuraRow } from './SimTuraRow';
import { SimulationStart } from './SimulationStart';
import { useSimulation, useSimulationActions } from './hooks';

// Clear of the sidebar on desktop
const POSITION = 'fixed bottom-4 left-4 z-40 md:bottom-6 md:left-[270px]';

/** Super admin only, and only where the server has the simulation on: the launcher bottom-left (Damis AI holds bottom-right), and a running session's panel */
export function SimulationPanel() {
  const { t } = useTranslation();
  const simulation = useSimulation();
  const [starting, setStarting] = useState(false);

  if (!simulation.data) return null;
  const { status, simTrucks } = simulation.data;

  if (!status) {
    return (
      <>
        <button
          type="button"
          onClick={() => setStarting(true)}
          data-qa-id="simulation-button"
          className={`${POSITION} flex h-12 items-center gap-1.5 rounded-full bg-primary px-4 text-sm font-semibold text-primary-foreground shadow-lg transition-transform hover:scale-105 motion-reduce:transition-none`}
        >
          <FlaskConical className="size-4" />
          {t('fleet.simulation.launcher')}
        </button>
        {starting && <SimulationStart simTrucks={simTrucks} onClose={() => setStarting(false)} />}
      </>
    );
  }

  return <SimulationSession status={status} />;
}

/** A running session: the clock, the steps and what the replay did to each copy */
function SimulationSession({ status }: { status: SimStatus }) {
  const { t, i18n } = useTranslation();
  const actions = useSimulationActions();
  const [open, setOpen] = useState(true);
  const [step, setStep] = useState<SimStep>('5m');
  const [wantsPlay, setPlaying] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const busy = actions.step.isPending || actions.restart.isPending || actions.setAi.isPending || actions.stop.isPending || actions.remove.isPending;
  const finished = status.state === 'finished';
  // Play ends by itself at the end of the day
  const playing = wantsPlay && !finished;
  const failed = (fallback: string) => (e: unknown) => {
    setPlaying(false);
    setError(toUserMessage(e, fallback));
  };

  const next = () => {
    setError(null);
    actions.step.mutate(step, { onError: failed(t('fleet.simulation.stepFailed')) });
  };

  // Play: the next step a moment after the last one has finished; the timer goes with a pause, a new status or unmount
  useEffect(() => {
    if (!playing) return;
    if (!canStep(status, busy)) return;
    const timer = setTimeout(next, PLAY_EVERY_MS);
    return () => clearTimeout(timer);
    // next closes over step and the mutation, which change with the deps below
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playing, status, busy, step]);

  const run = (start: () => void) => {
    setPlaying(false);
    setError(null);
    start();
  };

  const percent = Math.round(progressOf(status) * 100);
  const last = status.last;
  const ignition = last?.ignition == null ? t('fleet.simulation.ignitionUnknown') : t(last.ignition ? 'fleet.simulation.ignitionOn' : 'fleet.simulation.ignitionOff');

  return (
    <div
      className={`${POSITION} right-4 flex max-h-[70vh] flex-col rounded-lg border bg-background shadow-lg md:right-auto md:w-[380px]`}
      data-qa-id="simulation-panel"
    >
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="flex items-center gap-2 p-3 text-left text-sm"
        aria-expanded={open}
        data-qa-id="simulation-toggle"
      >
        <FlaskConical className="size-4 shrink-0" />
        <span className="font-medium tabular-nums">{formatDateTime(status.clock, i18n.language)}</span>
        <span className="text-muted-foreground">{t(`fleet.simulation.state.${status.state}`)}</span>
        {open ? <ChevronDown className="ml-auto size-4" /> : <ChevronUp className="ml-auto size-4" />}
      </button>

      {open && (
        <div className="space-y-3 overflow-y-auto border-t p-3">
          <div className="space-y-1">
            <div className="h-1.5 overflow-hidden rounded-full bg-muted" role="progressbar" aria-valuenow={percent} aria-valuemin={0} aria-valuemax={100}>
              <div className="h-full bg-primary transition-[width] motion-reduce:transition-none" style={{ width: `${percent}%` }} />
            </div>
            <p className="text-xs text-muted-foreground tabular-nums" data-qa-id="simulation-fed">
              {status.total === 0 ? t('fleet.simulation.noData') : t('fleet.simulation.fed', { fed: status.fed, total: status.total })}
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-1.5">
            <div className="flex rounded-md border p-0.5" role="group" aria-label={t('fleet.simulation.step')}>
              {STEPS.map((value) => (
                <button
                  key={value}
                  type="button"
                  onClick={() => setStep(value)}
                  aria-pressed={step === value}
                  className={`rounded px-2 py-1 text-xs font-medium ${step === value ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground'}`}
                >
                  {t(`fleet.simulation.steps.${value}`)}
                </button>
              ))}
            </div>
            <Button size="sm" disabled={!canStep(status, busy)} onClick={() => run(next)} data-qa-id="simulation-next">
              {actions.step.isPending ? <Loader2 className="size-4 animate-spin" /> : <SkipForward className="size-4" />}
              {t('fleet.simulation.next')}
            </Button>
            <Button size="sm" variant="outline" disabled={finished} onClick={() => setPlaying(!playing)} data-qa-id="simulation-play">
              {playing ? <Pause className="size-4" /> : <Play className="size-4" />}
              {playing ? t('fleet.simulation.pause') : t('fleet.simulation.play')}
            </Button>
          </div>

          {error && (
            <p className="text-xs text-status-danger-fg" role="alert" data-qa-id="simulation-error">
              {error}
            </p>
          )}

          {last && (
            <p className="text-xs text-muted-foreground tabular-nums" data-qa-id="simulation-last">
              {formatDateTime(last.at, i18n.language)} · {Math.round(last.speedKmh)} km/h · {ignition}
              {last.vehicleState ? ` · ${last.vehicleState}` : ''}
            </p>
          )}

          {status.turas.length > 0 && (
            <ul className="space-y-2">
              {status.turas.map((tura) => (
                <SimTuraRow key={tura.id} tura={tura} />
              ))}
            </ul>
          )}

          <label className="flex items-center gap-2 text-sm">
            <Checkbox
              checked={status.ai}
              disabled={busy}
              onCheckedChange={(checked) => {
                setError(null);
                actions.setAi.mutate(checked === true, { onError: failed(t('fleet.simulation.stepFailed')) });
              }}
              data-qa-id="simulation-ai"
            />
            {t('fleet.simulation.ai')}
          </label>

          <div className="flex flex-wrap items-center gap-1.5">
            <Button size="sm" variant="outline" disabled={busy} onClick={() => run(() => actions.restart.mutate(undefined, { onError: failed(t('fleet.simulation.stepFailed')) }))} data-qa-id="simulation-restart">
              <RotateCcw className="size-4" />
              {t('fleet.simulation.restart')}
            </Button>
            <Button size="sm" variant="outline" disabled={actions.stop.isPending} onClick={() => run(() => actions.stop.mutate(undefined, { onError: failed(t('fleet.simulation.stepFailed')) }))} data-qa-id="simulation-stop">
              {actions.stop.isPending ? <Loader2 className="size-4 animate-spin" /> : <Square className="size-4" />}
              {t('fleet.simulation.stop')}
            </Button>
            {confirmingDelete ? (
              <>
                <span className="text-xs">{t('fleet.simulation.deleteAsk')}</span>
                <Button
                  size="sm"
                  variant="destructive"
                  disabled={actions.remove.isPending}
                  onClick={() => run(() => actions.remove.mutate(status.simTruck.id, { onError: failed(t('fleet.simulation.deleteFailed')) }))}
                  data-qa-id="simulation-delete-confirm"
                >
                  {t('fleet.simulation.delete')}
                </Button>
                <Button size="sm" variant="ghost" onClick={() => setConfirmingDelete(false)}>
                  {t('fleet.simulation.cancel')}
                </Button>
              </>
            ) : (
              <Button size="sm" variant="ghost" onClick={() => setConfirmingDelete(true)} data-qa-id="simulation-delete">
                <Trash2 className="size-4" />
                {t('fleet.simulation.delete')}
              </Button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
